import { useCallback, useMemo, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { BreadcrumbItem } from '@rossum/components/breadcrumbs/Breadcrumbs';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { Header, HeaderTitle } from '@rossum/components/header/Header';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  IconLinkOff,
  IconToggleLeft,
  IconToggleRight,
} from '@rossum/ui/icons/tabler';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import { activitiesRulesPath } from '../../../containers/Activities/routes';
import { boldText, routerLink } from '../../../lib/formaterValues';
import { throwError, throwInfo } from '../../../redux/modules/messages/actions';
import { isEmpoweredManager as isEmpoweredManagerSelector } from '../../../redux/modules/user/selectors';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';
import { ruleManagerPath } from '../../rule-manager/routes';
import { LinkedQueuesChip } from '../components/LinkedQueuesChip';
import { MoveToAnotherQueueDialog } from '../components/MoveToAnotherQueueDialog';
import { RuleContextMenu } from '../components/RuleContextMenu';
import { useCreateRule } from '../hooks/useCreateRule';
import { useDeleteRule } from '../hooks/useDeleteRule';
import { useHandleDuplicateRule } from '../hooks/useHandleDuplicateRule';
import { hasMoveToAnotherQueueAction } from '../hooks/useMoveRule';
import { usePatchRule } from '../hooks/usePatchRule';
import { hasRemoveAction, useRemoveRule } from '../hooks/useRemoveRule';
import { useCombinedRuleQueueUrls } from '../hooks/useRuleQueue';
import { hasUnlinkAction, useUnlinkRule } from '../hooks/useUnlinkRule';
import { ruleErrorParser } from '../schemas/ruleErrorSchema';
import { extractKnownActions, ValidRuleForm } from '../utils';
import { RuleForm } from './RuleForm';
import { useRuleForm } from './useRuleForm';

const RULE_FORM_ID = 'rule_form';

export type RulePageProps = {
  // Rule is present on rule detail, undefined when creating
  rule?: QueueRule;
  // Queue is present on queue level, undefined on rule manager
  queue?: Queue;
  parentPath: string;
  renderBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => React.ReactNode;
  renderTabs?: () => React.ReactNode;
};

export const RulePage = ({
  rule,
  queue,
  parentPath,
  renderBreadcrumbs,
  renderTabs,
}: RulePageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const dispatch = useDispatch();

  const {
    mutate: patchRule,
    isLoading: isPatching,
    error: patchError,
  } = usePatchRule();

  const {
    mutate: createRule,
    isLoading: isCreating,
    error: createError,
  } = useCreateRule();

  const { mutate: deleteRule, isLoading: isDeleting } = useDeleteRule();
  const { mutate: handleDuplicate, isLoading: isDuplicating } =
    useHandleDuplicateRule();
  const { mutate: handleRemove, isLoading: isRemoving } = useRemoveRule(() => {
    history.replace(`${parentPath}/rules`);
  });

  const { mutate: unlinkRule, isLoading: isUnlinking } = useUnlinkRule({
    onSuccess: unlinkedRuleId => {
      history.replace(`${parentPath}/rules/${unlinkedRuleId}/detail`);
    },
    onUndoSuccess: originalRuleId => {
      history.replace(`${parentPath}/rules/${originalRuleId}/detail`);
    },
  });

  const isEmpoweredManager = useSelector(isEmpoweredManagerSelector);

  const isLinkedRule = useMemo(
    () => !!rule && hasUnlinkAction(queue, rule),
    [rule, queue]
  );

  const disabledEdit = isLinkedRule;

  const [moveQueueDialogOpen, setMoveQueueDialogOpen] = useState(false);

  // This useMemo is actually important, otherwise there is an infinite loop
  // Otherwise the reference to the form would keep changing.
  const errors = useMemo(() => {
    const parsedErrors = ruleErrorParser.safeParse(patchError ?? createError);
    return parsedErrors.success ? parsedErrors.data : {};
  }, [createError, patchError]);

  const { state } = useLocation<{ rule?: unknown } | undefined>();

  const formMethods = useRuleForm({
    rule,
    // @ts-expect-error TODO: what is wrong here?
    errors,
    disabled: disabledEdit,
    stateRule: state?.rule ?? undefined,
  });

  const hasErrors =
    rule && extractKnownActions(rule).actions.length !== rule.actions.length;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    formState: { isDirty },
  } = formMethods;

  const onDeleteSuccess = useCallback(() => {
    dispatch(throwInfo('ruleDeleteSuccess'));

    return history.replace(`${parentPath}/rules`);
  }, [dispatch, history, parentPath]);

  const onDeleteError = useCallback(
    () => dispatch(throwError('ruleDeleteFailed')),
    [dispatch]
  );

  const ruleManagerEnabled = useIsFeatureEnabled('rule-manager');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const buttons = [
    !rule ? null : (
      <Controller
        key="toggle-button-control"
        control={control}
        name="enabled"
        render={({ field }) => (
          <IconButton
            onClick={() => field.onChange(!field.value)}
            disabled={field.disabled}
          >
            {field.value ? (
              <SvgIcon fontSize="large" color="success">
                <IconToggleRight />
              </SvgIcon>
            ) : (
              <SvgIcon fontSize="large">
                <IconToggleLeft />
              </SvgIcon>
            )}
          </IconButton>
        )}
      />
    ),
    !rule ? null : (
      <RuleContextMenu
        key="rule-context-menu"
        disabled={
          isDuplicating ||
          isDirty ||
          isDeleting ||
          isPatching ||
          isCreating ||
          isRemoving ||
          isUnlinking
        }
        onDuplicate={() => handleDuplicate({ rule, queue })}
        onMoveToAnotherQueue={
          hasMoveToAnotherQueueAction(queue, rule)
            ? () => setMoveQueueDialogOpen(true)
            : undefined
        }
        onRemove={
          hasRemoveAction(queue, rule)
            ? () => handleRemove({ rule, queue })
            : undefined
        }
        ruleManagerPath={
          queue && !isEmpoweredManager
            ? `${ruleManagerPath()}/${rule.id}/detail`
            : undefined
        }
        ruleExecutionLogsUrl={
          isEmpoweredManager
            ? undefined
            : `${activitiesRulesPath()}?rule=${rule.id}`
        }
      />
    ),
    !rule ? null : hasUnlinkAction(queue, rule) ? (
      <Button
        key="unlink-rule-button"
        data-cy="unlink-rule-btn"
        variant="outlined"
        color="secondary"
        startIcon={
          isUnlinking ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            <SvgIcon fontSize="large">
              <IconLinkOff />
            </SvgIcon>
          )
        }
        disabled={isUnlinking}
        onClick={() => unlinkRule({ rule, queue })}
      >
        {intl.formatMessage({
          id: 'features.queueSettings.rules.list.itemContextMenu.item.unlink',
        })}
      </Button>
    ) : (
      <Button
        key="delete-rule-button"
        data-cy="delete-rule-btn"
        variant="outlined"
        color="secondary"
        startIcon={isDeleting && <CircularProgress color="inherit" size={20} />}
        disabled={isDeleting}
        onClick={() => setDeleteDialogOpen(true)}
      >
        {intl.formatMessage({
          id: 'features.queueSettings.rules.detail.delete',
        })}
      </Button>
    ),
    isLinkedRule ? (
      // Empowered managers can't reach the rule manager, so don't offer the
      // "Open in Manager" entry point for them.
      ruleManagerEnabled && !isEmpoweredManager ? (
        <Button
          key="open-in-manager-button"
          data-cy="open-in-manager-btn"
          variant="contained"
          component={RouterLink}
          to={`${ruleManagerPath()}/${rule?.id}/detail`}
        >
          {intl.formatMessage({
            id: 'features.queueSettings.rules.detail.openInManager',
          })}
        </Button>
      ) : null
    ) : (
      <Button
        key="save-rule-button"
        data-cy="save-rule-btn"
        variant="contained"
        type="submit"
        startIcon={
          (isPatching || isCreating) && (
            <CircularProgress color="inherit" size={20} />
          )
        }
        disabled={!isDirty || isPatching || isCreating}
        form={RULE_FORM_ID}
      >
        {intl.formatMessage({
          id: 'features.queueSettings.rules.detail.save',
        })}
      </Button>
    ),
  ];

  const name = watch('name');
  const description = watch('description');
  const createQueues = watch('queues');

  const onSubmit = ({ queues, ...payload }: ValidRuleForm) => {
    return rule
      ? patchRule(
          {
            patchedRuleId: rule.id,
            patchedRule: payload,
          },
          {
            onSuccess: () => {
              reset(getValues());
              dispatch(throwInfo('ruleUpdateSuccess'));
            },
            onError: () => dispatch(throwError('ruleUpdateFailed')),
          }
        )
      : createRule(
          {
            ...payload,
            queues: queue ? [queue.url] : (queues ?? []),
          },
          {
            onSuccess: createdRule => {
              reset(getValues());
              dispatch(throwInfo('ruleCreateSuccess'));
              // Defer navigation to allow state update to propagate
              // This prevents leaving dialog to be shown
              setTimeout(() => {
                history.replace(`${createdRule.id}/detail`);
              }, 0);
            },
            onError: () => dispatch(throwError('ruleCreationFailed')),
          }
        );
  };

  const breadcrumbs = useMemo(() => {
    return [
      {
        label:
          rule?.name ??
          intl.formatMessage({
            id: 'features.queueSettings.rules.newRule.title',
          }),
      },
    ];
  }, [intl, rule?.name]);

  const { queues } = useCombinedRuleQueueUrls({ rule });

  // This is the list of the queues for the rule
  // This basically picks the first non-empty item
  // Which is rule queues when editing, or the queues from the form state when creating
  const ruleQueueUrls = queue
    ? [queue.url]
    : ([queues, createQueues].find(a => a && a.length > 0) ?? []);

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          breadcrumbs={renderBreadcrumbs(breadcrumbs)}
          buttons={buttons}
          title={hasScrolled => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <HeaderTitle hasScrolled={hasScrolled} title={name} />
              {!hasScrolled && rule && !isEmpoweredManager && (
                <LinkedQueuesChip
                  queues={rule.queues}
                  isQueueLevel={!!queue}
                  to={`/settings/rule-manager/${rule.id}/queues`}
                />
              )}
            </Stack>
          )}
          description={description}
          tabs={renderTabs?.() ?? null}
        />
      )}
    >
      <Stack
        px={4}
        py={4}
        spacing={4}
        id={RULE_FORM_ID}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {disabledEdit ? (
          <Alert severity="info">
            {intl.formatMessage(
              {
                id: 'features.queueSettings.rules.disabledEdit',
              },
              {
                link: routerLink({
                  route: `${ruleManagerPath()}/${rule?.id}/detail`,
                }),
              }
            )}
          </Alert>
        ) : null}
        <FormProvider {...formMethods}>
          {hasErrors ? (
            <Alert variant="outlined" severity="warning">
              {intl.formatMessage({
                id: 'features.queueSettings.rules.invalidActions',
              })}
            </Alert>
          ) : null}

          {ruleQueueUrls.length > 0 ? (
            <RuleForm
              control={control}
              queue={queue}
              // Because of the length check above
              ruleQueueUrls={ruleQueueUrls as [Url, ...Url[]]}
            />
          ) : (
            <Alert severity="info">
              {intl.formatMessage({
                id: 'features.queueSettings.rules.noQueues',
              })}
            </Alert>
          )}
        </FormProvider>

        {/* decodedRule.success blocks it even when submitting so i removed it for now */}
        <LeavingDialog when={isDirty} />
        {rule ? (
          <DeleteConfirmationDialog
            title={intl.formatMessage({
              id: 'features.queueSettings.rules.deleteRuleConfirmation.title',
            })}
            description={intl.formatMessage(
              {
                id: 'features.queueSettings.rules.deleteRuleConfirmation.text',
              },
              {
                name,
                bold: boldText,
              }
            )}
            open={deleteDialogOpen}
            onCancel={() => setDeleteDialogOpen(false)}
            onConfirm={() => {
              setDeleteDialogOpen(false);
              deleteRule(
                {
                  deletedRuleId: rule.id,
                },
                {
                  onSuccess: onDeleteSuccess,
                  onError: onDeleteError,
                }
              );
            }}
          />
        ) : null}
      </Stack>
      {rule ? (
        <MoveToAnotherQueueDialog
          key={moveQueueDialogOpen.toString()}
          rule={rule}
          open={moveQueueDialogOpen}
          onClose={() => setMoveQueueDialogOpen(false)}
          onSuccess={() => {
            setMoveQueueDialogOpen(false);
            if (!queue) return;
            history.replace(`/queues/${queue.id}/settings/rules`);
          }}
        />
      ) : null}
    </PageLayout>
  );
};
